import React, { useState, useEffect, useRef } from 'react'

import classnames from 'classnames'

import { withStores } from 'utils/helpers.utils'

import { MAX_DESCRIPTION_LENGTH } from 'constants/communities.constants'
import { COVER_CROP_PARAMS } from 'constants/profile.constants'

import { notify } from 'libs/common/notify'
import { EloButton } from '@elo-kit/components/elo-button'
import { DivInput } from '@elo-kit/components/div-input/index'
import Input from 'ui-old/Input'

import CropImageModal from '../image-croping/CropImageModal'
import PostCover from '../PostCover'
import LabelsSelector from '../LabelsSelector'

import Avatar from '../avatar/Avatar'
import Button from '../button/Button'
import TextField from '../text-field/TextField'

import './create-post-form.scss'

const CreatePostForm = ({ communitiesStore, communityPostsStore, post, setEditPostMode, communityMemberStore }) => {
  const [cover, setCover] = useState({})
  const [editCoverModal, setEditCoverModal] = useState(false)
  const [selectedLabel, selectLabel] = useState(null)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [errors, setErrors] = useState([])
  const [isFocused, setIsFocused] = useState(false)
  const [openLinkEditor, setOpenLinkEditor] = useState(false)
  const [isContentEmpty, setIsContentEmpty] = useState(true)
  const [link, setCurrentLink] = useState({})
  const [isLinkValid, setIsLinkValid] = useState(false)
  const [linkToEdit, setLinkToEdit] = useState(null)
  const inputDivRef = useRef()

  useEffect(() => {
    if (!!post) {
      const {
        title: postTitle,
        content: postContent,
        cover: postCover,
        labels,
        coverCropH,
        coverCropW,
        coverCropX,
        coverCropY,
      } = post || {}

      setTitle(postTitle)
      setContent(postContent)
      setIsContentEmpty(postContent)
      setCover({
        imageUrl: postCover[0]?.original,
        croppedUrl: postCover[0]?.cropped,
        x: coverCropX,
        y: coverCropY,
        width: coverCropW,
        height: coverCropH,
        naturalWidth: coverCropW,
        naturalHeight: coverCropH,
      })
      if (labels?.length) {
        selectLabel({ label: labels[0].name, value: labels[0].discussionLabelId })
      }
      setIsFocused(true)
    }
  }, [])

  const toggleEditCoverModal = () => {
    setEditCoverModal(!editCoverModal)
  }

  const { width, height } = cover
  const isImageNotCropped = (!width || width === 0) && (!height || height === 0)

  const isExpanded = isFocused || !!title || !!content
  const formClassNames = classnames('post-form', {
    'post-form--expanded': isExpanded,
    'post-form--collapsed': !isExpanded,
  })

  const textFieldClassName = classnames('post-form__title-field', {
    'post-form__title-field--expanded': isExpanded,
  })

  const footerClassNames = classnames('post-form__footer', {
    'url-editor': openLinkEditor,
  })

  const clearForm = () => {
    setTitle('')
    setCover({})
    setContent('')
    selectLabel(null)
    setIsFocused(false)
    setIsContentEmpty(true)
  }

  const validate = (val) => {
    if (!val) {
      setErrors([I18n.t('react.shared.comminity.validations.content_required')])
    } else if (val.length > MAX_DESCRIPTION_LENGTH) {
      setErrors([I18n.t('react.shared.comminity.validations.message_too_long')])
    } else {
      setErrors([])
    }
  }

  const submit = async () => {
    const { id } = communitiesStore.item
    const { height, width, x, y, imageUrl, naturalWidth, naturalHeight } = cover
    const { id: postId, labels = [] } = post || {}

    let reqData = {
      communityId: id,
      title: title,
      content: content,
    }

    if (imageUrl) {
      const cropData = isImageNotCropped
        ? {
            avatarCropX: null,
            avatarCropY: null,
            avatarCropW: naturalWidth,
            avatarCropH: naturalHeight,
          }
        : {
            coverCropX: x,
            coverCropY: y,
            coverCropW: width,
            coverCropH: height,
          }

      const coverData = {
        coverRemoteUrl: imageUrl,
        ...cropData,
      }
      reqData = { ...reqData, ...coverData }
    }

    if (postId && !imageUrl) {
      reqData = { ...reqData, delete_cover: true }
    }

    if (postId && labels?.length) {
      const defaultArray = selectedLabel ? [{ discussionLabelId: selectedLabel.value }] : []
      reqData.labelsAttributes = labels.reduce(
        (prevLabel, label) => [
          ...prevLabel,
          {
            id: label.id,
            discussionLabelId: label.discussionLabelId,
            _destroy: selectedLabel ? selectedLabel.value !== label.discussionLabelId : true,
          },
        ],
        defaultArray
      )
    } else if (selectedLabel) {
      reqData.labelsAttributes = [{ discussionLabelId: selectedLabel.value }]
    }

    const submitAction = () =>
      postId
        ? communityPostsStore.updateItem(postId, reqData, { skipErrorNotific: true })
        : communityPostsStore.createItem(reqData, { skipErrorNotific: true })

    const resp = await submitAction()
    const { success, errorCode } = resp || {}

    if (!success && errorCode === 'muted') {
      notify('error', I18n.t('react.shared.community.notific.muted_payer'))
    }

    if (postId) {
      setEditPostMode(false)
    }

    clearForm()
  }

  const closeLinkEditor = () => {
    setLinkToEdit(null)
    setOpenLinkEditor(false)
    setCurrentLink({ href: '', label: '' })
  }

  const addLinkToContent = () => {
    if (linkToEdit) {
      const node = inputDivRef.current.querySelector(`#${linkToEdit}`)

      if (node) {
        node.href = link.href
        node.innerHTML = link.label || link.href
        node.title = link.href
        setContent(inputDivRef.current.innerHTML)
        setLinkToEdit(null)
        setIsContentEmpty(false)
      }
    } else {
      const element = `<a id='link${Date.now()}'  target="_blank" title='${link.href}' href='${link.href}'>${
        link.label || link.href
      }</a>`

      setContent(`${content} ${element}`)
      validate(`${content} ${element}`)
      setIsContentEmpty(false)
    }

    closeLinkEditor()
  }

  const onEditorClick = (e) => {
    let { tagName, href, innerHTML, id } = e.target || {}

    if (tagName === 'A') {
      setOpenLinkEditor(true)
      setCurrentLink({ href, label: innerHTML })
      setLinkToEdit(id)
    }
  }

  const { item: member } = communityMemberStore
  const { item: discussion = {} } = communitiesStore
  const { avatar: memberAvatar = [{}] } = member || {}

  const avatarUrl = memberAvatar[0]?.cropped || memberAvatar[0]?.original
  const labelsSelectorOptions = discussion?.labels?.map((item) => ({ label: item.name, value: item.id })) || []

  const isFormTouched =
    title !== '' ||
    content !== '' ||
    inputDivRef?.current?.textContent !== '' ||
    !!cover.imageUrl ||
    (labelsSelectorOptions.length > 0 && selectedLabel)

  return (
    <div className={formClassNames}>
      <TextField
        className={textFieldClassName}
        placeholder={I18n.t('react.cabinet.community.post_title_field_placeholder')}
        value={title}
        onChange={(value) => {
          setTitle(value)
          if (post?.id) {
            setIsContentEmpty(!content)
          }
        }}
      />
      <PostCover
        cover={cover}
        handleRemove={() => {
          setCover({})
          setIsContentEmpty(!content)
        }}
        croppedPostLink={isImageNotCropped ? cover.imageUrl : cover.croppedUrl}
      />
      <div className='post-form__textarea'>
        <Avatar imageUrl={avatarUrl} className='post-form__avatar' />
        <DivInput
          innerRef={inputDivRef}
          content={content}
          onInput={(e) => {
            validate(e.target.textContent)
            setIsContentEmpty(e.target.textContent.length === 0)
          }}
          onFocus={() => setIsFocused(true)}
          onClick={onEditorClick}
          onBlur={(e) => {
            setContent(e.target.innerHTML)
          }}
          placeholder={I18n.t('react.cabinet.community.share_something')}
        ></DivInput>
      </div>
      <div className={footerClassNames}>
        {errors.length > 0 && isFormTouched && <div className='post-form__errors'>{errors[0]}</div>}
        {!openLinkEditor && (
          <>
            <div className='post-form__actions'>
              <Button
                className='community_button--no-border post-form__action-button'
                disabled={cover.imageUrl}
                onClick={toggleEditCoverModal}
              >
                <i className='far fa-image' />
                {I18n.t('react.cabinet.community.add_image')}
              </Button>
              <Button
                className='community_button--no-border post-form__action-button'
                onClick={() => setOpenLinkEditor(true)}
              >
                <i className='far fa-link' />
                {I18n.t('react.cabinet.community.add_link')}
              </Button>
              {!!labelsSelectorOptions.length && (
                <LabelsSelector
                  className='post-form__labeles-selector'
                  options={labelsSelectorOptions}
                  onChange={(val) => {
                    selectLabel(val)
                    setIsContentEmpty(!!post ? false : isContentEmpty)
                  }}
                  value={selectedLabel}
                  placeholder={I18n.t('react.cabinet.community.post_selector_placeholder')}
                />
              )}
            </div>
            <div className='post-form__buttons'>
              <Button
                className='community_button--no-border'
                onClick={post?.id ? () => setEditPostMode(false) : clearForm}
              >
                {I18n.t('shared.common.cancel')}
              </Button>

              <Button
                className='community_button--wide'
                disabled={errors.length > 0 || isContentEmpty}
                onClick={submit}
              >
                {post?.id ? I18n.t('react.shared.button.save') : I18n.t('react.shared.button.submit_post')}
              </Button>
            </div>
          </>
        )}

        {openLinkEditor && (
          <>
            <Input
              class='form-control'
              placeholder={I18n.t('react.shared.comminity.placeholders.link')}
              value={link.href}
              validations={['urlWithProtocolOnly', 'required']}
              handleInput={(value) => setCurrentLink({ ...link, href: value })}
              handleValid={(value) => setIsLinkValid(value)}
            />
            <Input
              class='form-control'
              placeholder={I18n.t('react.shared.comminity.placeholders.label')}
              value={link.label}
              validations={['']}
              handleInput={(value) => setCurrentLink({ ...link, label: value })}
              handleValid={() => true}
            />
            <EloButton onClick={addLinkToContent} disabled={!isLinkValid} outlined>
              {I18n.t('react.shared.button.save')}
            </EloButton>
            <Button className='community_button--no-border' onClick={closeLinkEditor}>
              <i className='fas fa-times'></i>
            </Button>
          </>
        )}
      </div>
      <CropImageModal
        cropParams={COVER_CROP_PARAMS}
        image={cover.imageUrl}
        loaderLabel={I18n.t('react.shared.cover_image')}
        onCrop={(cropDetails, image) => {
          const imageDetails = {
            ...cropDetails,
            imageUrl: image,
          }
          setCover(imageDetails)
          setIsContentEmpty(!content)
        }}
        open={editCoverModal}
        toggleModal={toggleEditCoverModal}
      />
    </div>
  )
}

export default withStores(['communitiesStore', 'communityPostsStore', 'communityMemberStore'], CreatePostForm)
